import { useEffect, useRef, useState } from 'react';

interface UseVideoAutoplayOnViewParams {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  shouldAutoplay: boolean;
  shouldPauseOnExit: boolean;
  autoplayOnlyOnce?: boolean;
  threshold?: number;
  rootMargin?: string;
}

/**
 * Custom hook to handle video autoplay when the video element comes into view.
 * Uses IntersectionObserver to detect when the video is visible in the viewport.
 * Follows the same pattern as useImageOptimization.
 *
 * @param videoRef - Reference to the video element
 * @param shouldAutoplay - Whether the intersection observer should be set up
 * @param shouldPauseOnExit - Whether to pause the video when it exits the viewport
 * @param autoplayOnlyOnce - Whether to only autoplay once (default: true)
 * @param threshold - Intersection threshold (default: 0.1 - 10% visible)
 * @param rootMargin - Root margin for intersection observer (default: '0px')
 * @returns shouldAutoplay - Boolean indicating if video should autoplay
 */
export const useVideoAutoplayOnView = ({
  videoRef,
  shouldAutoplay,
  shouldPauseOnExit,
  autoplayOnlyOnce = false,
  threshold = 0.1,
  rootMargin = '0px'
}: UseVideoAutoplayOnViewParams) => {
  const [isAutoplayTriggered, setIsAutoplayTriggered] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Set up IntersectionObserver
  useEffect(() => {
    if (!shouldAutoplay && !shouldPauseOnExit) {
      return undefined;
    }

    const currentVideoRef = videoRef.current;
    if (!currentVideoRef) {
      return undefined;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting && entry.intersectionRatio >= threshold;
        const video = videoRef.current;

        if (isVisible && shouldAutoplay && (!autoplayOnlyOnce || !isAutoplayTriggered)) {
          if (video && video.paused) {
            if (!isAutoplayTriggered) {
              setIsAutoplayTriggered(true);
            }
            video.play().catch((error) => {
              // Silently handle autoplay failures (browser policy restrictions)
              // eslint-disable-next-line no-console
              console.warn('Video autoplay failed:', error);
            });
          }
        } else if (!isVisible && shouldPauseOnExit && isAutoplayTriggered) {
          if (video && !video.paused) {
            video.pause();
          }
        }
      },
      {
        root: null,
        rootMargin,
        threshold: [threshold]
      }
    );

    observerRef.current.observe(currentVideoRef);
    return () => observerRef.current?.disconnect();
  }, [
    videoRef,
    shouldAutoplay,
    shouldPauseOnExit,
    autoplayOnlyOnce,
    threshold,
    rootMargin,
    isAutoplayTriggered
  ]);

  return isAutoplayTriggered;
};
